"use client"

import Link from "next/link"
import { Bell, Check, ChevronDown, Globe2, Menu, Moon, Search, ShoppingBag, Sun, User, X } from "lucide-react"
import { useEffect, useRef, useState } from "react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import type { NavigationAuthState, NavigationCta, NavigationItem, NavigationSocialLink } from "./navigation/navigation-types"

export type MobileMenuIslandProps = {
  brand: string
  items: NavigationItem[]
  cta?: { label: string; href: string }
  ctas?: NavigationCta[]
  socials?: NavigationSocialLink[]
  showSearch?: boolean
}

export function MobileMenuIsland({ brand, items, cta, ctas = cta ? [cta] : [], socials = [], showSearch = false }: MobileMenuIslandProps) {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const dialogRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const previousOverflow = document.body.style.overflow
    const trigger = triggerRef.current
    document.body.style.overflow = "hidden"
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false)
    }
    window.addEventListener("keydown", handleKeyDown)
    const focusable = dialogRef.current?.querySelector<HTMLElement>("a, button, input, select, textarea, [tabindex]:not([tabindex='-1'])")
    focusable?.focus()
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener("keydown", handleKeyDown)
      trigger?.focus()
    }
  }, [open])

  const primaryCta = ctas[0]
  const secondaryCta = ctas[1]

  return (
    <div className="md:hidden">
      <Button ref={triggerRef} type="button" variant="ghost" size="icon" aria-label="Open menu" aria-expanded={open} onClick={() => setOpen(true)}>
        <Menu className="size-5" />
      </Button>
      {open && (
        <div className="fixed inset-0 z-[100] bg-foreground/55 backdrop-blur-md" onClick={() => setOpen(false)}>
          <div
            className="ml-auto flex min-h-dvh w-full flex-col overflow-y-auto border-l border-border/70 bg-background px-5 pb-[max(4.5rem,env(safe-area-inset-bottom))] pt-[max(1.25rem,env(safe-area-inset-top))] text-foreground shadow-elegant sm:max-w-sm sm:px-6 sm:pb-[max(1.25rem,env(safe-area-inset-bottom))]"
            role="dialog"
            aria-modal="true"
            aria-label="Mobile navigation"
            ref={dialogRef}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex min-h-12 items-center justify-between gap-4 border-b border-border/70 pb-4">
              <div className="min-w-0">
                <p className="truncate font-heading text-xl font-black uppercase tracking-tight text-primary">{brand}</p>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Commercial boiler service</p>
              </div>
              <Button type="button" variant="ghost" size="icon" aria-label="Close menu" className="shrink-0 rounded-button bg-muted text-foreground hover:bg-muted/80" onClick={() => setOpen(false)}>
                <X className="size-5" />
              </Button>
            </div>
            {showSearch && (
              <label className="mt-5 flex items-center gap-2 rounded-card border border-border/70 bg-card px-4 py-3 text-sm text-muted-foreground">
                <Search className="size-4" />
                <input className="min-w-0 flex-1 bg-transparent outline-none placeholder:text-muted-foreground" placeholder="Search pages" />
              </label>
            )}
            <nav aria-label="Mobile navigation" className="grid gap-2 py-6">
              {items.map((item, index) => (
                <MobileMenuItem key={item.href} item={item} index={index} onNavigate={() => setOpen(false)} />
              ))}
            </nav>
            {(primaryCta || secondaryCta || socials.length > 0) && (
              <div className="mt-auto grid gap-3 border-t border-border/70 pt-4">
                {secondaryCta && (
                  <Button asChild size="lg" variant="outline" className="h-12 w-full rounded-full">
                    <Link href={secondaryCta.href} onClick={() => setOpen(false)}>{secondaryCta.label}</Link>
                  </Button>
                )}
                {primaryCta && (
                  <Button asChild size="lg" className="h-12 w-full rounded-full shadow-[var(--shadow-elegant)]">
                    <Link href={primaryCta.href} onClick={() => setOpen(false)}>{primaryCta.label}</Link>
                  </Button>
                )}
                {socials.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    {socials.map((social) => <Link key={social.href} href={social.href} className="rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-muted-foreground">{social.label}</Link>)}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function MobileMenuItem({ item, index, onNavigate }: { item: NavigationItem; index: number; onNavigate: () => void }) {
  const [expanded, setExpanded] = useState(false)
  const nestedItems = item.dropdownItems ?? item.dropdownColumns?.flatMap((column) => column.items) ?? []

  if (nestedItems.length === 0) {
    return (
      <Link href={item.href} onClick={onNavigate} className="flex min-h-14 items-center justify-between rounded-card border border-border/70 bg-card px-4 text-left text-base font-semibold text-foreground shadow-sm transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        <span className="inline-flex items-center gap-2">{item.label}{item.badge && <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[0.65rem] uppercase tracking-[0.12em] text-primary">{item.badge}</span>}</span>
        <span className="text-sm tabular-nums text-muted-foreground">0{index + 1}</span>
      </Link>
    )
  }

  return (
    <div className="rounded-card border border-border/70 bg-card shadow-sm">
      <button type="button" className="flex min-h-14 w-full items-center justify-between px-4 text-left text-base font-semibold text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-expanded={expanded} onClick={() => setExpanded(!expanded)}>
        <span>{item.label}</span>
        <ChevronDown className={cn("size-4 text-muted-foreground transition-transform", expanded && "rotate-180")} />
      </button>
      {expanded && (
        <div className="grid gap-1 border-t border-border/70 p-2">
          <Link href={item.href} onClick={onNavigate} className="rounded-xl px-3 py-2 text-sm font-semibold text-primary hover:bg-muted">Overview</Link>
          {nestedItems.map((nestedItem) => (
            <Link key={nestedItem.href} href={nestedItem.href} onClick={onNavigate} className="rounded-xl px-3 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground">
              {nestedItem.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}

export function NavSearchIsland({ placeholder = "Search" }: { placeholder?: string }) {
  const [query, setQuery] = useState("")

  return (
    <label className="hidden items-center gap-2 rounded-full border border-border/70 bg-card px-3 py-1.5 text-sm text-muted-foreground lg:flex">
      <Search className="size-4" />
      <Input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder={placeholder}
        className="h-7 w-32 border-0 bg-transparent p-0 text-sm shadow-none focus-visible:ring-0"
      />
    </label>
  )
}

export function NavigationScrollStateIsland({ targetId }: { targetId: string }) {
  useEffect(() => {
    const target = document.getElementById(targetId)
    if (!target) return
    let previousY = window.scrollY
    const update = () => {
      const currentY = window.scrollY
      target.dataset.scrolled = String(currentY > 12)
      target.dataset.scrollDir = currentY > previousY && currentY > 80 ? "down" : "up"
      previousY = currentY
    }
    update()
    window.addEventListener("scroll", update, { passive: true })
    return () => window.removeEventListener("scroll", update)
  }, [targetId])

  return null
}

export function CommandPaletteTriggerIsland({ items }: { items: NavigationItem[] }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="relative hidden lg:block">
      <Button type="button" variant="outline" size="sm" className="rounded-full" aria-expanded={open} aria-label="Open command palette" onClick={() => setOpen(!open)}>
        <Search className="size-4" />
        Command
      </Button>
      {open && (
        <div className="absolute right-0 top-full z-50 mt-3 w-80 rounded-card border border-border bg-background p-2 shadow-elegant">
          <div className="flex items-center gap-2 border-b border-border px-3 py-2 text-sm text-muted-foreground"><Search className="size-4" />Jump to</div>
          <div className="mt-2 grid gap-1">
            {items.slice(0, 6).map((item) => <Link key={item.href} href={item.href} className="rounded-xl px-3 py-2 text-sm font-medium hover:bg-muted" onClick={() => setOpen(false)}>{item.label}</Link>)}
          </div>
        </div>
      )}
    </div>
  )
}

export function ThemeToggleIsland() {
  const [theme, setTheme] = useState<"light" | "dark">("light")
  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark")
  }, [theme])
  return <Button type="button" variant="ghost" size="icon" className="hidden rounded-full lg:inline-flex" aria-label="Toggle theme" onClick={() => setTheme(theme === "dark" ? "light" : "dark")}>{theme === "dark" ? <Sun className="size-4" /> : <Moon className="size-4" />}</Button>
}

export function LocaleCurrencySwitcherIsland() {
  const [value, setValue] = useState("US / USD")
  return (
    <label className="hidden items-center gap-2 rounded-full border border-border/70 bg-card px-3 py-2 text-xs font-semibold text-muted-foreground lg:flex">
      <Globe2 className="size-4" />
      <select className="bg-transparent outline-none" value={value} onChange={(event) => setValue(event.target.value)} aria-label="Locale and currency">
        <option>US / USD</option>
        <option>EU / EUR</option>
        <option>UK / GBP</option>
      </select>
    </label>
  )
}

export function UtilityIconsIsland({ showCart, showAccount, cartCount = 0, auth }: { showCart?: boolean; showAccount?: boolean; cartCount?: number; auth?: NavigationAuthState }) {
  return (
    <div className="hidden items-center gap-1 lg:flex">
      {showCart && <Button type="button" variant="ghost" size="icon" className="relative rounded-full" aria-label={`Cart with ${cartCount} items`}><ShoppingBag className="size-4" />{cartCount > 0 && <span className="absolute -right-1 -top-1 flex size-5 items-center justify-center rounded-full bg-primary text-[0.65rem] font-bold text-primary-foreground">{cartCount}</span>}</Button>}
      {showAccount && <Button type="button" variant="ghost" size="icon" className="rounded-full" aria-label={auth?.signedIn ? "Open account menu" : "Sign in"}>{auth?.signedIn ? <Check className="size-4" /> : <User className="size-4" />}</Button>}
      {showAccount && auth?.signedIn && <Button type="button" variant="ghost" size="icon" className="rounded-full" aria-label="Notifications"><Bell className="size-4" /></Button>}
    </div>
  )
}